import { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  TablePagination,
  alpha,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import SearchIcon from '@mui/icons-material/Search';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import type { Location, Inventory } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';

interface InventoryWithLocation extends Inventory {
  locationName?: string;
}

const fetchOrganizationInventory = async () => {
  const response = await axiosInstance.get('/inventory/organization');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations/admin/all');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const exportColumns: ColumnConfig[] = [
  {
    field: 'productName',
    headerName: 'Product',
    valueFormatter: (_, row) => row.variant?.product?.name || row.product?.name || 'Unknown',
  },
  {
    field: 'productSku',
    headerName: 'SKU',
    valueFormatter: (_, row) => row.variant?.product?.sku || row.product?.sku || '-',
  },
  {
    field: 'locationName',
    headerName: 'Location',
    valueFormatter: (_, row) => row.locationName || row.locationId || 'Unknown',
  },
  {
    field: 'locationType',
    headerName: 'Type',
    valueFormatter: (value) => (value === 'warehouse' ? 'Warehouse' : 'Store'),
  },
  { field: 'quantity', headerName: 'Stock' },
  { field: 'reserved', headerName: 'Reserved' },
  { field: 'allocated', headerName: 'Allocated' },
  {
    field: 'available',
    headerName: 'Available',
    valueFormatter: (_, row) => (row.quantity || 0) - (row.reserved || 0) - (row.allocated || 0),
  },
  { field: 'lowStockThreshold', headerName: 'Low Stock Alert' },
];

export const InventoryList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const productIdFilter = searchParams.get('productId');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<InventoryWithLocation | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove' | 'set'>('add');
  const [adjustmentQuantity, setAdjustmentQuantity] = useState<number>(0);
  const [lowStockThreshold, setLowStockThreshold] = useState<number>(10);
  const [deleteReason, setDeleteReason] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const { data: inventoryData = [], isLoading: inventoryLoading } = useQuery({
    queryKey: ['inventory', 'organization'],
    queryFn: fetchOrganizationInventory,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const locationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((location) => map.set(location.id, location.name));
    return map;
  }, [locations]);

  const filterOptions: FilterOption[] = useMemo(() => {
    const locationOptions = locations.map((location) => ({
      value: location.id,
      label: `${location.name} (${location.type === 'warehouse' ? 'Warehouse' : 'Store'})`,
    }));

    return [
      {
        field: 'stockLevel',
        label: 'Stock Level',
        type: 'select',
        options: [
          { value: 'out_of_stock', label: 'Out of Stock' },
          { value: 'low_stock', label: 'Low Stock' },
          { value: 'in_stock', label: 'In Stock' },
        ],
      },
      {
        field: 'locationType',
        label: 'Location Type',
        type: 'select',
        options: [
          { value: 'store', label: 'Store' },
          { value: 'warehouse', label: 'Warehouse' },
        ],
      },
      {
        field: 'locationId',
        label: 'Location',
        type: 'select',
        options: locationOptions,
      },
    ];
  }, [locations]);

  const enrichedData = useMemo((): InventoryWithLocation[] => {
    return inventoryData.map((item: Inventory) => ({
      ...item,
      locationName: locationNameMap.get(item.locationId) || item.locationId,
    }));
  }, [inventoryData, locationNameMap]);

  const filteredData = useMemo(() => {
    return enrichedData.filter((item: InventoryWithLocation) => {
      if (productIdFilter && item.productId !== productIdFilter) {
        return false;
      }

      if (searchValue) {
        const search = searchValue.toLowerCase();
        const productName = item.product?.name || '';
        const productSku = item.product?.sku || '';
        const locationName = item.locationName || '';
        const matchesSearch =
          productName.toLowerCase().includes(search) ||
          productSku.toLowerCase().includes(search) ||
          locationName.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      if (activeFilters.stockLevel) {
        const available = (item.quantity || 0) - (item.reserved || 0) - (item.allocated || 0);
        const threshold = item.lowStockThreshold || 10;

        if (activeFilters.stockLevel === 'out_of_stock' && available > 0) return false;
        if (activeFilters.stockLevel === 'low_stock' && (available <= 0 || available > threshold)) return false;
        if (activeFilters.stockLevel === 'in_stock' && available <= threshold) return false;
      }

      if (activeFilters.locationType && item.location?.type !== activeFilters.locationType) {
        return false;
      }

      if (activeFilters.locationId && item.locationId !== activeFilters.locationId) {
        return false;
      }

      return true;
    });
  }, [enrichedData, searchValue, activeFilters, productIdFilter]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const isLoading = inventoryLoading;

  const adjustMutation = useMutation({
    mutationFn: ({ productId, values }: { productId: string; values: any }) =>
      axiosInstance.patch(`/inventory/product/${productId}`, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      showMessage('Inventory adjusted successfully', 'success');
      handleCloseAdjustDialog();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to adjust inventory', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ productId, reason }: { productId: string; reason?: string }) =>
      axiosInstance.delete(`/inventory/product/${productId}`, { data: { reason } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      showMessage('Inventory deleted successfully', 'success');
      handleCloseDeleteDialog();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to delete inventory', 'error');
    },
  });

  const handleOpenAdjustDialog = (inventory: InventoryWithLocation) => {
    setSelectedInventory(inventory);
    setAdjustmentType('add');
    setAdjustmentQuantity(0);
    setLowStockThreshold(inventory.lowStockThreshold || 10);
    setAdjustDialogOpen(true);
  };

  const handleCloseAdjustDialog = () => {
    setAdjustDialogOpen(false);
    setSelectedInventory(null);
  };

  const handleOpenDeleteDialog = (inventory: InventoryWithLocation) => {
    setSelectedInventory(inventory);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedInventory(null);
    setDeleteReason('');
  };

  const handleDeleteInventory = () => {
    if (!selectedInventory) return;

    deleteMutation.mutate({
      productId: selectedInventory.productId,
      reason: deleteReason || undefined,
    });
  };

  const handleAdjustStock = () => {
    if (!selectedInventory || adjustmentQuantity === 0) return;

    let newQuantity = selectedInventory.quantity;

    if (adjustmentType === 'add') {
      newQuantity += adjustmentQuantity;
    } else if (adjustmentType === 'remove') {
      newQuantity -= adjustmentQuantity;
    } else if (adjustmentType === 'set') {
      newQuantity = adjustmentQuantity;
    }

    if (newQuantity < 0) {
      showMessage('Stock quantity cannot be negative', 'error');
      return;
    }

    adjustMutation.mutate({
      productId: selectedInventory.productId,
      values: {
        quantity: newQuantity,
        lowStockThreshold,
      },
    });
  };

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredProduct = productIdFilter
    ? enrichedData.find((item) => item.productId === productIdFilter)?.product
    : null;

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredData.length;
    const outOfStock = filteredData.filter(
      (item) => (item.quantity || 0) - (item.reserved || 0) - (item.allocated || 0) <= 0
    ).length;
    const lowStock = filteredData.filter((item) => {
      const available = (item.quantity || 0) - (item.reserved || 0) - (item.allocated || 0);
      const threshold = item.lowStockThreshold || 10;
      return available > 0 && available <= threshold;
    }).length;
    const totalValue = filteredData.reduce((sum, item) => sum + (item.quantity || 0), 0);

    return { total, outOfStock, lowStock, totalValue };
  }, [filteredData]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<InventoryWithLocation>[] = [
    {
      field: 'product',
      headerName: 'Product',
      render: (value, row) => (
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 0.5,
              lineHeight: 1.2,
            }}
          >
            {row.product?.name || 'Unknown'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: typography.fontFamily.mono,
              color: colors.textMuted,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.wide,
            }}
          >
            SKU: {row.product?.sku || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.location?.type === 'warehouse' ? (
            <WarehouseIcon sx={{ fontSize: 18, color: palette.primary }} />
          ) : (
            <StoreIcon sx={{ fontSize: 18, color: palette.info }} />
          )}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
                color: colors.textPrimary,
                fontSize: typography.fontSize.sm,
              }}
            >
              {row.locationName || row.location?.name || row.locationId}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                color: colors.textMuted,
                fontSize: typography.fontSize.xs,
                textTransform: 'uppercase',
              }}
            >
              {row.location?.type === 'warehouse' ? 'Warehouse' : 'Store'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'quantity',
      headerName: 'Total',
      render: (value) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.75,
            borderRadius: theme.radius.xs,
            backgroundColor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.extrabold,
              color: colors.textPrimary,
            }}
          >
            {value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'reserved',
      headerName: 'Reserved',
      render: (value) =>
        value > 0 ? (
          <Chip
            label={value}
            sx={{
              backgroundColor: alpha(palette.warning, 0.1),
              color: palette.warning,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${palette.warning}`,
              minWidth: 50,
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              color: colors.textMuted,
            }}
          >
            -
          </Typography>
        ),
    },
    {
      field: 'allocated',
      headerName: 'Allocated',
      render: (value) =>
        value > 0 ? (
          <Chip
            label={value}
            sx={{
              backgroundColor: alpha(palette.info, 0.1),
              color: palette.info,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              border: `1px solid ${palette.info}`,
              minWidth: 50,
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.sm,
              color: colors.textMuted,
            }}
          >
            -
          </Typography>
        ),
    },
    {
      field: 'available',
      headerName: 'Available',
      render: (value, row) => {
        const available = (row.quantity || 0) - (row.reserved || 0) - (row.allocated || 0);
        const threshold = row.lowStockThreshold || 10;
        const isOutOfStock = available <= 0;
        const isLowStock = available > 0 && available <= threshold;

        return (
          <Box
            sx={{
              display: 'inline-flex',
              px: 2,
              py: 0.75,
              borderRadius: theme.radius.xs,
              backgroundColor: isOutOfStock
                ? alpha(palette.error, 0.1)
                : isLowStock
                ? alpha(palette.warning, 0.1)
                : alpha(palette.success, 0.1),
              border: `2px solid ${
                isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.success
              }`,
            }}
          >
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.extrabold,
                color: isOutOfStock ? palette.error : isLowStock ? palette.warning : palette.success,
              }}
            >
              {available}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Industrial Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            WAREHOUSE CONTROL
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              INVENTORY
              {filteredProduct && (
                <Typography
                  component="span"
                  variant="h5"
                  sx={{
                    ml: 2,
                    color: colors.textMuted,
                    fontWeight: typography.fontWeight.medium,
                  }}
                >
                  / {filteredProduct.name}
                </Typography>
              )}
            </Typography>
            {productIdFilter && (
              <Button
                size="small"
                onClick={() => navigate('/app/inventory')}
                sx={{
                  mt: 1,
                  textTransform: 'uppercase',
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  color: palette.primary,
                }}
              >
                ← Clear Filter
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app/inventory/create')}
            sx={{
              backgroundColor: palette.primary,
              color: '#FFFFFF',
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.sm,
              px: 3,
              py: 1.5,
              borderRadius: theme.radius.sm,
              ...animations.buttonPress,
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.8),
              },
            }}
          >
            Add Stock
          </Button>
        </Box>
      </Box>

      {/* Stats Dashboard - Industrial Control Panel Style */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            ...animations.hoverLift,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.primary,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                TOTAL ITEMS
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: colors.textPrimary,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.total}
              </Typography>
            </Box>
            <InventoryIcon sx={{ fontSize: 40, color: alpha(palette.primary, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            ...animations.hoverLift,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.error,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                OUT OF STOCK
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.error,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.outOfStock}
              </Typography>
            </Box>
            <ErrorOutlineIcon sx={{ fontSize: 40, color: alpha(palette.error, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            ...animations.hoverLift,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.warning,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                LOW STOCK
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.warning,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.lowStock}
              </Typography>
            </Box>
            <TrendingDownIcon sx={{ fontSize: 40, color: alpha(palette.warning, 0.2) }} />
          </Box>
        </Card>

        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 2.5,
            position: 'relative',
            overflow: 'hidden',
            ...animations.hoverLift,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: palette.success,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                TOTAL UNITS
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  color: palette.success,
                  lineHeight: 1,
                  mt: 1,
                }}
              >
                {stats.totalValue.toLocaleString()}
              </Typography>
            </Box>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: alpha(palette.success, 0.2) }} />
          </Box>
        </Card>
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.2),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH INVENTORY..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="inventory"
          />
        </Box>
      </Card>

      {/* Inventory Table - Industrial Ledger Style */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.3),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <InventoryIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO INVENTORY FOUND
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              No items match your search criteria
            </Typography>
          </Box>
        ) : (
          <>
            <ResponsiveTable
              columns={tableColumns}
              rows={paginatedData}
              loading={isLoading}
              emptyMessage="No inventory items match your search criteria"
              primaryField="product"
              getPrimaryText={(row) => row.product?.name || 'Unknown'}
              onEdit={(item: InventoryWithLocation) => handleOpenAdjustDialog(item)}
              onDelete={(item: InventoryWithLocation) => {
                const hasReservedOrAllocated = (item.reserved || 0) > 0 || (item.allocated || 0) > 0;
                if (!hasReservedOrAllocated) {
                  handleOpenDeleteDialog(item);
                }
              }}
            />

            {/* Industrial Pagination */}
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                borderTop: `2px solid ${colors.border}`,
              }}
            >
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  },
                  '& .MuiSelect-select': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.bold,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* Adjust Stock Dialog */}
      <Dialog
        open={adjustDialogOpen}
        onClose={handleCloseAdjustDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.primary}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Adjust Stock
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.textMuted,
                  fontFamily: typography.fontFamily.mono,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.widest,
                }}
              >
                Current Stock
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: colors.textPrimary,
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.extrabold,
                  mt: 0.5,
                }}
              >
                {selectedInventory?.quantity || 0}
              </Typography>
            </Box>

            <TextField
              select
              fullWidth
              label="Adjustment Type"
              value={adjustmentType}
              onChange={(e) => setAdjustmentType(e.target.value as any)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  fontFamily: typography.fontFamily.mono,
                },
              }}
            >
              <MenuItem value="add">Add Stock</MenuItem>
              <MenuItem value="remove">Remove Stock</MenuItem>
              <MenuItem value="set">Set Stock</MenuItem>
            </TextField>

            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={adjustmentQuantity}
              onChange={(e) => setAdjustmentQuantity(parseInt(e.target.value) || 0)}
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  fontFamily: typography.fontFamily.mono,
                },
              }}
            />

            <TextField
              fullWidth
              label="Low Stock Threshold"
              type="number"
              value={lowStockThreshold}
              onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 10)}
              inputProps={{ min: 0 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                  fontFamily: typography.fontFamily.mono,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={handleCloseAdjustDialog}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdjustStock}
            variant="contained"
            disabled={adjustMutation.isPending || adjustmentQuantity === 0}
            sx={{
              backgroundColor: palette.primary,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            {adjustMutation.isPending ? 'Adjusting...' : 'Adjust Stock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `2px solid ${palette.error}`,
            borderRadius: theme.radius.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: palette.error,
            textTransform: 'uppercase',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          Delete Inventory
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" color="error" sx={{ fontWeight: typography.fontWeight.bold }}>
              Are you sure you want to delete this inventory record?
            </Typography>
            <Box
              sx={{
                backgroundColor: alpha(palette.error, 0.1),
                border: `1px solid ${palette.error}`,
                borderRadius: theme.radius.sm,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                Product: <strong>{selectedInventory?.product?.name || 'Unknown'}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                Location: <strong>{selectedInventory?.locationName || 'Unknown'}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Current Stock: <strong>{selectedInventory?.quantity || 0}</strong> units
              </Typography>
            </Box>
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: typography.fontWeight.medium }}>
              This action cannot be undone. The inventory deletion will be recorded in the movement history.
            </Typography>
            <TextField
              fullWidth
              label="Reason for deletion (optional)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              multiline
              rows={2}
              placeholder="Enter a reason for this deletion..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.surfaceElevated,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteInventory}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            sx={{
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              fontFamily: typography.fontFamily.mono,
            }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Inventory'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
